/**
 * Ads Manager — shared REST payload types for dashboard API + customer app.
 * Composed from {@link CreateMarketingAdRequest} / campaign request fragments in
 * `KYDMarketing` so guided and launch wires stay aligned with persisted models.
 *
 * - GET `.../campaigns/:id/guided-edit` — campaign-scoped load; server picks the first editable ad set for the payload anchor.
 * - GET/PUT `.../campaigns/:id/adsets/:adsetId/guided-edit` — explicit ad set (required for PUT).
 * - POST `.../ads/campaigns/launch`
 */
import type {
  AudienceData,
  MarketingAdCreativeGuidedWireBase,
  MarketingAdsetTargetingLocations,
  MarketingCampaignBudgetDineroPayload,
  MarketingCampaignBudgetSegment,
} from "./KYDMarketing";

// ---------------------------------------------------------------------------
// Schedule (guided + launch) — wall-clock strings, not ad set ISO schedule
// ---------------------------------------------------------------------------

/** Shared by GET/PUT guided payload, update body, and POST launch. */
export type GuidedCampaignScheduleWire = {
  start_at: string;
  end_at: string;
};

/** Default IANA timezone when the linked event has no timezone — guided-edit schedule math and display. */
export const GUIDED_EDIT_DEFAULT_TIMEZONE = "America/New_York";

// ---------------------------------------------------------------------------
// Targeting
// ---------------------------------------------------------------------------

type AdsetGeoMarketRow = NonNullable<
  MarketingAdsetTargetingLocations["geo_markets"]
>[number];

/**
 * Guided REST `locations` — extends {@link MarketingAdsetTargetingLocations} by
 * allowing optional `country` on geo markets (form) and optional `name` (display).
 */
export type GuidedEditTargetingLocations = Omit<
  MarketingAdsetTargetingLocations,
  "geo_markets"
> & {
  geo_markets?: Array<
    Omit<AdsetGeoMarketRow, "country"> & { country?: string; name?: string }
  >;
};

/** Targeting object returned by GET guided-edit. */
export type GuidedEditTargetingGet = {
  strategy: "template" | "custom";
  custom_audiences?: AudienceData[];
  resolved_strategies?: { id: string; name: string; description: string }[];
  /** Persisted ad set locations for edit-mode form hydration. */
  locations?: GuidedEditTargetingLocations;
  demographics?: {
    age_min?: number;
    age_max?: number;
    genders?: string;
  };
  interests?: { key: string; value: string }[];
};

/** Targeting fields accepted on PUT guided-edit (superset of GET for create/new-adset flows). */
export type GuidedEditTargetingUpdate = {
  strategy: "custom" | "template";
  custom_audiences?: AudienceData[];
  locations?: GuidedEditTargetingLocations;
  demographics?: {
    age_min?: number;
    age_max?: number;
    genders?: string;
  };
  interests?: { key: string; value: string }[];
  /**
   * Create flow / launch (Smart Targeting off): org `MetaAdConfig.targeting_strategies` ids
   * to materialize as **additional** ad sets. Requires `strategy: "custom"` on the wire with
   * Smart Targeting off; not “manual-only” — `enabled_strategy_ids` alone satisfies launch validation
   * when no interests/locations/custom audiences are set.
   */
  enabled_strategy_ids?: string[];
};

// ---------------------------------------------------------------------------
// Creatives — wire shape → {@link CreateMarketingAdCreativeShape} via transformers
// ---------------------------------------------------------------------------

/**
 * Placement URLs/keys + Meta identity on the wire (not on full
 * `CreateMarketingAdCreativeShape` in KYDMarketing until mapped).
 */
export type GuidedCreativePlacementAndIdentityWire = {
  square_url?: string;
  square_file_key?: string;
  square_media_type?: "image" | "video";
  portrait_url?: string;
  portrait_file_key?: string;
  portrait_media_type?: "image" | "video";
  fb_page_id: string;
  instagram_id: string;
};

export type InstagramPartnerPermissionStatusWire =
  | "APPROVED"
  | "PENDING"
  | "REVOKED"
  | "UNKNOWN";

/** Instagram partnership (partner post) fields on guided creative wire rows. */
export type GuidedInstagramPartnershipWire = {
  partner_post_enabled?: boolean;
  partner_instagram_user_id?: string;
  partner_instagram_username?: string;
  partner_permission_status?: InstagramPartnerPermissionStatusWire;
  partner_facebook_page_id?: string;
  /** Linked IG on the partner Facebook page in Meta; used to validate FB+IG consistency at launch. */
  partner_facebook_linked_instagram_user_id?: string;
};

/**
 * One creative row: GET response shape and PUT `creatives[]` item.
 * Copy fields align with {@link MarketingAdCreativeGuidedWireBase} / future
 * `CreateMarketingAdRequest.creative`; CTA, link, and `media` are filled server-side.
 */
export type GuidedEditCreativeApi = MarketingAdCreativeGuidedWireBase &
  GuidedCreativePlacementAndIdentityWire &
  GuidedInstagramPartnershipWire;

// ---------------------------------------------------------------------------
// Budget — PUT uses campaign dinero payload (relaxed amount for partial clears)
// ---------------------------------------------------------------------------

/**
 * PUT guided-edit `budget`. Based on {@link MarketingCampaignBudgetDineroPayload}
 * with optional-null `amount.amount` for partial updates.
 *
 * **`amount.amount` is minor units (e.g. cents for USD)** — same as GET `GuidedEditPayload.budget.amount`
 * and persisted `MarketingCampaign.budget.amount.amount`.
 * For `type: "scheduled"`, send `segments` only (full replace). Scheduled maps to Meta **lifetime** only.
 */
export type GuidedEditUpdateBudget = {
  type?: "daily" | "lifetime" | "scheduled";
  amount?: Partial<{ amount: number; currency: string }> & {
    amount?: number | null;
  };
  /** Minor units per segment `amount`; ISO `time_start` / `time_end`. */
  segments?: MarketingCampaignBudgetSegment[] | null;
};

/**
 * GET guided-edit campaign budget.
 * `amount` / `lifetime` are **minor units (cents for USD)**, matching persisted campaign budget.
 * For `scheduled`, `segments` are phased caps; Meta base/schedules are derived at publish.
 */
export type GuidedEditPayloadBudgetDisplay =
  | {
      type: "lifetime" | "daily";
      amount: number;
      lifetime?: number;
    }
  | {
      type: "scheduled";
      segments: Array<{
        time_start: string;
        time_end: string;
        amount: number;
        currency: string;
      }>;
    };

// ---------------------------------------------------------------------------
// Payloads
// ---------------------------------------------------------------------------

/** Response body for GET `.../campaigns/:id/guided-edit` or GET `.../adsets/:adsetId/guided-edit`. */
export type GuidedEditPayload = {
  event_id: string;
  ad_account_id: string;
  campaign_id: string;
  /** Meta Graph campaign id when the campaign was published at least once. */
  campaign_external_id?: string | null;
  adset_id: string;
  budget: GuidedEditPayloadBudgetDisplay;
  schedule: GuidedCampaignScheduleWire;
  targeting: GuidedEditTargetingGet;
  creatives: GuidedEditCreativeApi[];
  pixel_id?: string;
  /** Event timezone (IANA). Schedule wall times use this zone; always matches the linked event — not editable via PUT. */
  timezone?: string;
  adset_name?: string;
};

/**
 * JSON body for PUT .../guided-edit (excluding path params).
 */

export type GuidedEditUpdateRequestBody = {
  budget?: GuidedEditUpdateBudget;
  schedule?: GuidedCampaignScheduleWire;
  targeting?: GuidedEditTargetingUpdate;
  creatives?: GuidedEditCreativeApi[];
  creative_propagation_mode?: "replace_creatives" | "append_last_creative";
  /** Client/API should normalize with `normalizeGuidedEditTargetAdsetIds` in `common/utils/guidedEditTargetAdsets`. */
  target_adset_ids?: string[];
  adset_name?: string;
};

/**
 * Guided-edit form slice used when building campaign/adset/creative updates
 * (no multi-adset propagation flags).
 */
export type GuidedEditPayloadInput = Omit<
  GuidedEditUpdateRequestBody,
  "creative_propagation_mode" | "target_adset_ids"
>;

/**
 * Guided-edit PUT fields + `campaign_id`, excluding `creatives` and `budget`
 * so callers can supply wire or form shapes without impossible intersections.
 */
export type GuidedSubmitSaveParamsCore = Omit<
  GuidedEditUpdateRequestBody,
  "creatives" | "budget"
> & {
  campaign_id: string;
};

/** Save slice with wire creatives and budget (API / server). */
export type GuidedSubmitSaveParams = GuidedSubmitSaveParamsCore & {
  creatives: GuidedEditCreativeApi[];
  budget?: GuidedEditUpdateBudget;
};

/**
 * Shared input for building guided-edit submit params from draft mode + save slice.
 * Defaults match the API wire; the dashboard may use form creatives and a looser budget shape until map-to-API.
 */
export type GuidedSubmitBuildInput<
  TCreative = GuidedEditCreativeApi,
  TBudget = GuidedEditUpdateBudget,
> = {
  saveParams: GuidedSubmitSaveParamsCore & {
    creatives: TCreative[];
    budget?: TBudget;
  };
  targetAdsetIds?: string[];
};

// ---------------------------------------------------------------------------
// POST /ads/campaigns/launch
// ---------------------------------------------------------------------------

export type LaunchCampaignCreativeApi = GuidedEditCreativeApi & {
  fb_page_name?: string;
  instagram_name?: string;
};

/** POST launch targeting — same as guided GET without `resolved_strategies`. */
export type LaunchCampaignTargetingWire = Pick<
  GuidedEditTargetingGet,
  "strategy" | "custom_audiences" | "locations" | "demographics" | "interests"
> & {
  /**
   * Each id adds an ad set from org `targeting_strategies`. Used with `strategy: "custom"` (Smart Targeting off).
   * May accompany the synthetic “custom” user-audience ad set or, when there are no user custom audiences,
   * still produce ad sets from these strategies alone.
   */
  enabled_strategy_ids?: string[];
};

/** POST launch budget — minor units on amounts; same shape as {@link GuidedEditPayloadBudgetDisplay}. */
export type LaunchCampaignBudgetWire = GuidedEditPayloadBudgetDisplay;

export type LaunchCampaignRequestBody = {
  event_id: string;
  ad_account_id: string;
  budget: LaunchCampaignBudgetWire;
  schedule: GuidedCampaignScheduleWire;
  creatives: LaunchCampaignCreativeApi[];
  targeting: LaunchCampaignTargetingWire;
  pixel_id?: string;
};
